/**
 * Permission hooks (SC-439): show or hide UI from the signed-in person's effective permissions.
 * UX only; the server and cloud check every action themselves.
 *
 * While the answer is still loading, a gated control is hidden (so it never flashes) and the
 * caller can tell, so it can show a placeholder rather than treating that as "no". Controls
 * that every member has today are not gated at all, so members never wait on this.
 */
import { useEffect, useMemo } from 'react'
import { PERMISSION_KEYS, type PermissionKey } from '../../../types/rbac/catalogue'
import { useRbacStore } from '../stores/rbacStore'
import { usePermissionStore } from '../stores/permissionStore'
import { resolvePermission, surfaceRequirement } from '../utils/rbacPermissions'

export interface PermissionState {
  allowed: boolean
  /** True while the answer is still coming; `allowed` is false until it arrives. */
  loading: boolean
}

/** Server-verified platform admin; the same flag the admin menu and routes trust. */
export function useElevatedAdmin(): boolean {
  return usePermissionStore(
    (s) => s.adminCapabilitiesVerified && (s.isGlobalAdmin || s.isPlatformAdmin)
  )
}

/**
 * Server-verified global admin: the same gate (`meetsGlobalAdminRouteGate`) as `requireGlobalAdmin`,
 * which guards the owner-management routes. `useElevatedAdmin` also accepts the softer admin-access
 * gate, which those routes refuse, so Owner controls use this one.
 */
export function useGlobalAdmin(): boolean {
  return usePermissionStore((s) => s.adminCapabilitiesVerified && s.isGlobalAdmin)
}

export function usePermissionState(
  keys: PermissionKey | readonly PermissionKey[] | null
): PermissionState {
  const status = useRbacStore((s) => s.status)
  const me = useRbacStore((s) => s.me)
  const elevated = useElevatedAdmin()
  return useMemo(() => {
    if (keys === null) return { allowed: false, loading: false }
    const verdict = resolvePermission(keys, { status, me }, elevated)
    return { allowed: verdict === true, loading: verdict === 'pending' }
    // `keys` is compared by content: callers pass literals, which are new arrays every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(keys) ? keys.join('|') : keys, status, me, elevated])
}

export function usePermission(key: PermissionKey): boolean {
  return usePermissionState(key).allowed
}

/** True when the person holds at least one of the keys. */
export function useAnyPermission(keys: readonly PermissionKey[]): boolean {
  return usePermissionState(keys).allowed
}

/**
 * A control identified by its id in `types/rbac/surfaces.ts`: the id names the permission, so the
 * control and the catalogue's "this unlocks ..." list can't disagree.
 */
export function useSurfaceState(surfaceId: string): PermissionState {
  return usePermissionState(surfaceRequirement(surfaceId))
}

export function useSurface(surfaceId: string): boolean {
  return useSurfaceState(surfaceId).allowed
}

/** The company's Owner: the only person who may transfer ownership. */
export function useIsCompanyOwner(): boolean {
  return useRbacStore((s) => s.status === 'ready' && s.me?.isOwner === true)
}

/** What the person holds in the current company, for code that compares keys itself. */
export function useMyPermissionKeys(): readonly string[] {
  return useRbacStore((s) => (s.status === 'ready' && s.me ? s.me.permissions : EMPTY))
}

const EMPTY: readonly string[] = []

/**
 * The permissions to judge "can I give this on?" against: a platform admin holds every live key
 * (the server treats them so), everyone else what GET /api/rbac/me says.
 */
export function useActorPermissionKeys(): readonly string[] {
  const mine = useMyPermissionKeys()
  const elevated = useElevatedAdmin()
  return elevated ? PERMISSION_KEYS : mine
}

const FOCUS_REFRESH_MIN_AGE_MS = 60_000

/**
 * Keeps the store on the signed-in person's company: loads it when the company is known, settles
 * on "none" once it is known there is no company, and asks again when the window regains focus
 * (someone else may have changed this person's access meanwhile). Mounted once, by the main menu.
 */
export function useRbacCompanySync(input: {
  userId: string | null
  companyId: string | null
  /** False until the company lookup has finished, so "no company yet" is not read as "none". */
  companyResolved: boolean
  /**
   * The lookup failed and is being retried (SC-477): the company is not known to be missing, so
   * "none" must not be settled, or route guards would send the person away. Ignored once a
   * company id is known.
   */
  companyUnknown?: boolean
}): void {
  const { userId, companyId, companyResolved, companyUnknown = false } = input

  useEffect(() => {
    const store = useRbacStore.getState()
    if (!userId) {
      store.clear()
      return
    }
    if (!companyResolved) return
    if (companyUnknown && !companyId) return
    // The catalogue is not fetched here: the screens that list permissions ask for it when they
    // open, and labels fall back to the compiled catalogue. That saves a call per page load.
    void store.ensureCompany(companyId)
  }, [userId, companyId, companyResolved, companyUnknown])

  useEffect(() => {
    if (!userId || !companyId) return
    const onFocus = () => {
      void useRbacStore.getState().refresh({ maxAgeMs: FOCUS_REFRESH_MIN_AGE_MS })
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [userId, companyId])
}

/**
 * Loads the store for a company the caller already knows, for screens that can open before the
 * main menu has resolved it (the onboarding invite step).
 */
export function useEnsureRbacCompany(companyId: string | null | undefined): void {
  useEffect(() => {
    if (!companyId) return
    void useRbacStore.getState().ensureCompany(companyId)
  }, [companyId])
}
